// app/analytics/PageView.tsx

/**
 * ---------------------------------------------------------------------------
 * <PageView/> - GA4 "virtual page-view" bridge for the App Router
 * ---------------------------------------------------------------------------
 * • Declared as a **Client Component** (`"use client"`), so it runs only
 *   in the browser after hydration.
 *
 * • Uses `usePathname()` to react to every client-side navigation that the
 *   App Router performs (links, `router.push`, `<Link />`, etc.).
 *
 * • After the initial bootstrap pageview is owned by `injectGA4()`, later
 *   pathname changes call
 *
 *       gtag('event', 'page_view', { page_path, page_title, page_location })
 *
 *   which records a **page_view** in Google Analytics 4 with the fresh App
 *   Router pathname metadata *without* causing a full reload.
 *
 * • Query-string changes (filters, search params) are intentionally ignored at
 *   the pageview layer; track them via custom GA events instead.
 *   - When `NEXT_PUBLIC_GA_ID` is absent the effect is a no-op, so the app
 *     works fine in local dev and test environments.
 *
 * • User properties (`last_subject`, `last_level`) are derived from the
 *   pathname and queued through the GA helper so audience segmentation is
 *   preserved even if GA finishes booting after the first render.
 *
 * • The component renders **`null`** because its only purpose is the
 *   side-effect; mount it once (e.g. in `app/layout.tsx`) and forget about it.
 *
 *   <body>
 *     {children}
 *     <SpeedInsights />
 *     <PageView />            ← one-time mount here
 *   </body>
 * ---------------------------------------------------------------------------
 */

"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { isGaReady, pageview, setUserProperties } from "@/lib/gtag";
import { waitForDocumentTitle } from "@/app/analytics/waitForDocumentTitle";

/** Derive the academic subject from the current pathname. */
function getSubjectFromPath(pathname: string): string | null {
  if (pathname.includes("chemistry")) return "chemistry";
  if (pathname.includes("physics")) return "physics";
  if (pathname.includes("biology")) return "biology";
  if (pathname.includes("maths") || pathname.includes("math"))
    return "mathematics";
  if (pathname.includes("scholarship") || pathname.includes("bursary"))
    return "financial_aid";
  return null;
}

/** Derive the academic level from the current pathname. */
function getLevelFromPath(pathname: string): string | null {
  if (pathname.includes("h2-") || pathname.includes("a-level")) return "h2";
  if (pathname.includes("ip-")) return "ip";
  if (pathname.includes("o-level") || pathname.includes("olevel"))
    return "o_level";
  return null;
}

export default function PageView() {
  const pathname = usePathname();
  const isFirstRender = useRef(true);
  const previousTitle = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname) return;

    // Set user properties for GA4 audience segmentation
    const subject = getSubjectFromPath(pathname);
    const level = getLevelFromPath(pathname);

    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousTitle.current =
        typeof document !== "undefined" ? document.title : null;
      if (subject || level) {
        const props: Record<string, string> = {};
        if (subject) props.last_subject = subject;
        if (level) props.last_level = level;
        setUserProperties(props);
      }
      return;
    }

    if (!isGaReady()) {
      previousTitle.current =
        typeof document !== "undefined" ? document.title : null;
      return;
    }

    if (subject || level) {
      const props: Record<string, string> = {};
      if (subject) props.last_subject = subject;
      if (level) props.last_level = level;
      setUserProperties(props);
    }

    const abortController = new AbortController();
    void (async () => {
      await waitForDocumentTitle({
        previousTitle: previousTitle.current,
        signal: abortController.signal,
      });
      if (abortController.signal.aborted) return;
      pageview(pathname);
      previousTitle.current =
        typeof document !== "undefined" ? document.title : null;
    })();

    return () => {
      abortController.abort();
    };
  }, [pathname]);

  return null;
}
